import { faArrowRotateRight } from '@fortawesome/free-solid-svg-icons/faArrowRotateRight';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import { styled } from '@mui/material/styles';
import { OfferCard } from 'components/organisms/OfferCard';
import { useRouter } from 'next/router';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  signUpPopupEnabledAtom,
  signUpPopupOpenAtom,
  userAtom,
} from 'recoil/atoms';
import Rest from 'services/rest';
import {
  AuctionFromSearch,
  GetAuctionsRequest,
  GetAuctionsResponse,
} from 'shared/types/api/getAuctions';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import {
  Autoplay as SwiperAutoplay,
  Pagination as SwiperPagination,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { StyledFontAwesomeIconMui5 } from '../../../components/atoms/FontAwesomeIcon';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { useFilters } from '../../../services/filters/hooks/useFilters';
import {
  cleanObject,
  getFiltersFromUrl,
} from '../../../services/filters/utils';
import { AUCTIONS_PER_PAGE } from '../../../shared/utils/constants';
import DealsToolbar from './DealsToolbar';

const rest = new Rest();

const DealsNumberText = styled('span')(
  ({
    theme: {
      palette: { primary, grey },
    },
  }) => ({
    fontSize: '1rem',
    color: grey[700],
    '& strong': {
      color: primary.main,
    },
  }),
);

const Footer = styled('div')(
  ({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing(2, 0)};`,
);

const StyledSwiper = styled(Swiper, {
  shouldForwardProp: (prop) => prop !== 'isCompact',
})<{
  isCompact?: boolean;
}>(({ theme, isCompact }) => {
  const third = 1 / 3;
  return {
    '&.swiper': {
      padding: theme.spacing(isCompact ? third : 3, third, third, third),
      margin: theme.spacing(isCompact ? -third : -3, -third, -third, -third),
      '& .swiper-pagination': {
        ...(isCompact ? { bottom: theme.spacing(1) } : { top: 0 }),
        height: 'fit-content',
      },
    },
    '& .swiper-pagination-bullet ': {
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
    },
    '& .swiper-slide': {
      marginRight: theme.spacing(3),
      '@media (min-width: 660px)': {
        maxWidth: `calc(50% - ${theme.spacing(1.5)})`,
      },
      '@media (min-width: 1280px)': {
        maxWidth: `calc(100% / 3 - ${theme.spacing(2)})`,
      },
    },
  };
});

const getAuctions = (rest: Rest, params: GetAuctionsRequest) => {
  try {
    return rest.getAuctions(params);
  } catch (e) {
    return Promise.resolve({
      perPage: AUCTIONS_PER_PAGE,
      auctions: [],
      count: 0,
      dynamicFilters: {},
    } as GetAuctionsResponse);
  }
};

type AuctionsListItem = {
  auctions: AuctionFromSearch[];
};

export function getShouldIgnoreProcessedImage(set: Set<string>) {
  return function shouldIgnoreProcessedImage({
    offer: { processed_image },
  }: AuctionFromSearch) {
    if (!processed_image) {
      return false;
    }

    if (set.has(processed_image)) {
      return true;
    }

    set.add(processed_image);
    return false;
  };
}

const Body: FC<BodyPropTypes> = ({
  auctions,
  featuredAuctions,
  auctionsCount,
  perPage,
  onOpenFiltersRequest,
  zipcode,
}): JSX.Element => {
  const router = useRouter();

  const { setFilterValue, filters } = useFilters();

  const [isLoading, toggleLoading] = useState(false);
  const [isDealsToolbarSticky, setIsDealsToolbarSticky] = useState(false);
  const setSignUpPopupOpenAtom = useSetRecoilState(signUpPopupOpenAtom);

  const signUpPopupEnabled = useRecoilValue(signUpPopupEnabledAtom);

  const offset = filters.offset || 0;
  const pages = Math.ceil(auctionsCount / perPage);
  const user = useRecoilValue(userAtom);

  const [isFirstRender, setIsFirstRender] = useState(true);
  const [dynamicOffset, setDynamicOffset] = useState(offset);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const [allAuctions, setAllAuctions] = useState<AuctionsListItem[]>([
    { auctions },
  ]);

  const [dealNumber, setDealNumber] = useState(auctions.length);

  useEffect(() => {
    const offset = filters.offset || 0;
    const page = Math.ceil((offset + 1) / perPage);
    const end = Math.min(page * perPage, auctionsCount);
    const start = (page - 1) * perPage + 1;
    setDealNumber(end - start + 1);
  }, [filters, perPage, auctionsCount]);

  useEffect(() => {
    if (isFirstRender) {
      setAllAuctions([{ auctions }]);
      setDynamicOffset(offset);
    }
  }, [auctions, offset, isFirstRender]);

  const dealsNumber = `${dealNumber} of ${auctionsCount} deals`;

  const loadMoreAuctions = useCallback(async () => {
    const { query } = router;
    const filtersFromUrl = getFiltersFromUrl(query, true);
    const isZipcodeInQuery = 'zipcode' in query;
    if (!isZipcodeInQuery) {
      filtersFromUrl.zipcode = user?.zipcode || null;
    }
    const getAuctionRequest: GetAuctionsRequest = cleanObject(filtersFromUrl);
    const nextOffset = dynamicOffset + perPage;
    if (dynamicOffset >= auctionsCount) {
      return;
    }

    setIsLoadingNext(true);
    const nextAuctionRequest = {
      ...getAuctionRequest,
      offset: nextOffset,
    };
    const nextAuctions = await getAuctions(rest, nextAuctionRequest);
    setAllAuctions((prev) => [...prev, { auctions: nextAuctions.auctions }]);
    setDynamicOffset(nextOffset);
    setIsLoadingNext(false);
    setDealNumber(dealNumber + nextAuctions.auctions.length);
  }, [
    router,
    dynamicOffset,
    perPage,
    auctionsCount,
    dealNumber,
    setSignUpPopupOpenAtom,
    user?.zipcode,
  ]);

  const virtualPage = Math.ceil((dynamicOffset + 1) / perPage);
  const isLoadMoreDisabled = dynamicOffset + perPage >= auctionsCount;

  useEffect(() => {
    const handleLoadingStart = () => toggleLoading(true);
    const handleLoadingComplete = () => toggleLoading(false);
    const handleLoadingError = () => toggleLoading(false);

    router?.events.on('routeChangeStart', handleLoadingStart);
    router?.events.on('routeChangeComplete', handleLoadingComplete);
    router?.events.on('routeChangeError', handleLoadingError);

    return () => {
      router?.events.off('routeChangeStart', handleLoadingStart);
      router?.events.off('routeChangeComplete', handleLoadingComplete);
      router?.events.off('routeChangeError', handleLoadingError);
    };
  }, [router?.events]);

  const { isExtraSmallScreen } = useScreenSize();

  const uniqueProcessedImages = new Set<string>();

  const shouldIgnorePocessedImage = getShouldIgnoreProcessedImage(
    uniqueProcessedImages,
  );

  const featuredIgnoreProcessedImage = featuredAuctions.map(
    shouldIgnorePocessedImage,
  );

  const allIgnoreProcessedImage = allAuctions.map(({ auctions }) =>
    auctions.map(shouldIgnorePocessedImage),
  );

  let imagePriorityCount = 0;
  const featuredImagePriority = featuredAuctions.map(() => {
    imagePriorityCount += 1;
    return imagePriorityCount <= 3;
  });
  const allImagePriority = allAuctions.map(({ auctions }) =>
    auctions.map(() => {
      imagePriorityCount += 1;
      return imagePriorityCount <= 3;
    }),
  );

  return (
    <div id='results'>
      <InView
        onChange={(inView) => {
          setIsDealsToolbarSticky(!inView);
        }}
        style={{
          height: 65,
          marginTop: -65,
        }} // should not be less than navbar height which is 65px
        fallbackInView={true}
        threshold={1}
      />
      <DealsToolbar
        sticky={isDealsToolbarSticky}
        auctionsCount={auctionsCount}
        onFiltersClick={() => onOpenFiltersRequest()}
        zipcode={zipcode}
      />
      {featuredAuctions.length > 0 && (
        <>
          <StyledSwiper
            loop
            rewind={true}
            autoplay={{
              delay: 10_000,
              pauseOnMouseEnter: true,
              disableOnInteraction: true,
              waitForTransition: true,
            }}
            slidesPerView={'auto'}
            pagination={{ clickable: true }}
            modules={[SwiperPagination, SwiperAutoplay]}
            isCompact={isExtraSmallScreen}
            spaceBetween={0}
          >
            <div className='swiper-wrapper'>
              {featuredAuctions.map((auction, itemIndex) => (
                <SwiperSlide key={auction.id}>
                  <div className={`js-limited-offer-card-${itemIndex}`}>
                    <OfferCard
                      auction={auction}
                      extendHeight={isExtraSmallScreen}
                      ignoreProcessedImage={
                        featuredIgnoreProcessedImage[itemIndex]
                      }
                      imagePriority={featuredImagePriority[itemIndex]}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </div>
          </StyledSwiper>
          <Divider sx={(t) => ({ marginY: `${t.spacing(2)} !important` })} />
        </>
      )}
      <Grid container spacing={3}>
        {allAuctions.map((pageItem, chunkIndex) => (
          <Fragment key={chunkIndex}>
            {pageItem.auctions.map((auction, itemIndex) => (
              <Grid
                className={`js-offer-card-${chunkIndex}-${itemIndex}`}
                item
                lg={4}
                md={6}
                sm={6}
                xs={12}
                key={auction.id}
              >
                <OfferCard
                  auction={auction}
                  ignoreFeatured={!filters.is_featured}
                  ignoreProcessedImage={
                    allIgnoreProcessedImage[chunkIndex][itemIndex]
                  }
                  imagePriority={allImagePriority[chunkIndex][itemIndex]}
                />
              </Grid>
            ))}
          </Fragment>
        ))}
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            sx={{
              '& Button': {
                marginBottom: 1,
              },
            }}
          >
            <Button
              color='primary'
              variant='contained'
              onClick={loadMoreAuctions}
              disabled={isLoadingNext || isLoadMoreDisabled}
            >
              Show More
              <StyledFontAwesomeIconMui5
                style={{ marginLeft: 8 }}
                icon={faArrowRotateRight}
                spin={isLoadingNext}
                // @ts-expect-error size is defined but typescript doesn't know about it
                size='md'
              />
            </Button>
            <DealsNumberText>
              Showing <strong>{dealsNumber}</strong>
            </DealsNumberText>
          </Box>
        </Grid>
      </Grid>
      <Footer>
        <Pagination
          count={pages}
          siblingCount={0}
          page={virtualPage}
          disabled={signUpPopupEnabled ?? (isLoading || isLoadingNext)}
          renderItem={(item) => {
            const query = { ...router.query } as any;
            const page = item.page > 0 ? item.page - 1 : item.page;
            const offset = page * perPage;
            if (!offset) {
              delete query.offset;
            } else {
              query.offset = `${offset}`;
            }

            const params = new URLSearchParams(query).toString();
            const href = params.length
              ? `${router.pathname}?${params}`
              : router.pathname;

            return <PaginationItem {...item} href={href} component={'a'} />;
          }}
          onChange={(e, value) => {
            e.preventDefault();
            setFilterValue('offset', (value - 1) * perPage);
            setIsFirstRender(true);
          }}
        />
      </Footer>
    </div>
  );
};

type BodyPropTypes = {
  auctions: AuctionFromSearch[];
  featuredAuctions: AuctionFromSearch[];
  auctionsCount: number;
  perPage: number;
  onOpenFiltersRequest: () => void;
  zipcode: string;
};

export default Body;
